<template>
  <LeftGradientBlockLayoutBody>
    <template slot="left">
      <ValueInput v-model.trim="form_data.amount"
                  placeholder="0.00"
                  :label=" isIncomeDeal ? $t('valueIncomeLabel') : $t('valueExpenseLabel')"
                  :currency_id="instrumentCurrencyId"
                  :class="{'--error':$v.form_data.amount.$error }">
        <template v-slot:errors="">
          <div class="error" v-if="!$v.form_data.amount.required">{{ $t('errors.required') }}</div>
        </template>
      </ValueInput>
    </template>
    <template slot="right">
      <BaseSelect v-model="form_data.asset" :options="assetsList"
                  :select-label="$t('assetsLabel')"
                  :class="{'--error':$v.form_data.asset.$error}"
                  label="name"
                  track-by="id"
                  :allow-empty="false"
                  @input="form_data.asset = $event">
        <template v-slot:errors>
          <div class="error" v-if="$v.form_data.asset.$invalid">{{ $t('errors.required') }}</div>
        </template>
      </BaseSelect>
      <BaseTextarea v-model="form_data.note"
                    :label="$t('noteLabel')"
                    class="col-span-2"
                    placeholder=""/>
      <BaseButton :theme="isIncomeDeal ? 'green' : 'red'"
                  :loading="submitted"
                  @click="submit">
        {{ isIncomeDeal ? $t('buttonIncomeLabel') : $t('buttonExpenseLabel') }}
      </BaseButton>
      <div class="flex items-center">
        <VueCtkDateTimePicker id="transaction-date"
                              class="inline"
                              v-model="form_data.date"
                              format="YYYY-MM-DD"
                              :formatted="dateFormatted"
                              :locale="$i18n.locale"
                              :auto-close="true"
                              :only-date="true"
                              :no-label="true"
                              :no-clear-button="true"
                              :no-button="true"
                              color="#2D4C5E"/>
        <VueCtkDateTimePicker id="transaction-time"
                              class="inline"
                              v-model="form_data.time"
                              format="HH:mm"
                              formatted="HH:mm"
                              :locale="$i18n.locale"
                              :only-time="true"
                              :no-clear-button="true"
                              :no-label="true"
                              :no-button="true"
                              color="#2D4C5E"/>
      </div>
    </template>
  </LeftGradientBlockLayoutBody>
</template>

<script>
import LeftGradientBlockLayoutBody
  from "@/components/layouts/cabinet/LeftGradientBlockLayout/LeftGradientBlockLayoutBody";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseTextarea from "@/components/UI/BaseTextarea";
import {decimal, required} from "vuelidate/lib/validators";
import {mapActions, mapGetters, mapState} from "vuex";
import {dealTypes} from "@/utilities/invest_deal_types";

export default {
  name: "ManualDealsForm",
  components: {
    BaseTextarea,
    BaseButton,
    BaseSelect,
    ValueInput,
    LeftGradientBlockLayoutBody
  },
  props: {
    deal_type: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      form_data: {
        amount: null,
        asset: null,
        note: '',
        date: this.$moment().format('YYYY-MM-DD'),
        time: this.$moment().format('HH:mm'),
      },
      submitted: false
    }
  },
  computed: {
    ...mapState('investments/assets', ['grouped_assets_list']),
    ...mapState('investments/portfolios', ['current_portfolio']),
    ...mapGetters('cabinet/currencies', ['getCurrencyByCode']),
    dateFormatted() {
      const isToday = this.$moment(this.form_data.date, 'YYYY-MM-DD').startOf('day').unix() === this.$moment().startOf('day').unix()

      return isToday ? this.$t('today') : 'DD.MM.YYYY'
    },
    isIncomeDeal() {
      return !![dealTypes.TYPE_INCOME_DIVIDEND, dealTypes.TYPE_INCOME_COUPON].includes(this.deal_type);
    },
    assetsList() {
      switch (this.deal_type) {
        case dealTypes.TYPE_INCOME_DIVIDEND:
          return this.grouped_assets_list.data.find(item => item.properties.type === 1)?.assets || [];
        case dealTypes.TYPE_INCOME_COUPON:
          return this.grouped_assets_list.data.find(item => item.properties.type === 2)?.assets || [];
        default:
          return this.grouped_assets_list.data.find(item => item.properties.type === 4)?.assets || [];
      }
    },
    instrumentCurrencyId() {
      if (!!this.form_data.asset) {
        return this.getCurrencyByCode(this.form_data.asset.currency).id
      } else {
        return '';
      }
    },
  },
  validations: {
    form_data: {
      amount: {
        required,
        decimal,
        minValue: 0.01,
      },
      asset: {
        required
      },
    },
  },
  methods: {
    ...mapActions('investments/deals', ['createDeal']),
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.submitted = true;
          let data = {
            portfolio_id: this.current_portfolio.data.id,
            type: this.deal_type,
            instrument_id: this.form_data.asset.instrument.id,
            price: 1,
            amount: +this.form_data.amount,
            note: this.form_data.note,
            date_time: `${this.form_data.date} ${this.form_data.time}:00`,
            timezone: this.$moment.tz.guess(),
          }

          const res = await this.createDeal(data);
          if (res.body.success) {
            this.$toasted.success('Операция успешно добавлена')
            await this.$store.dispatch('investments/assets/getAssets', this.current_portfolio.data.id)
            await this.$store.dispatch('investments/assets/getGroupedAssets', this.current_portfolio.data.id)
            this.$emit('success')
            this.$v.$reset();
          } else {
            this.$toasted.error('Ошибка! Операция не добавлена.')
            console.log(res)
          }
        } catch (err) {
          this.$toasted.error('Ошибка! Операция не добавлена.')
          console.log(err)
        } finally {
          this.submitted = false;
        }
      }
    }
  },
  watch: {
    deal_type(val) {
      if (val === dealTypes.TYPE_INCOME_DIVIDEND || val === dealTypes.TYPE_INCOME_COUPON) {
        this.form_data.asset = null;
      }
      this.$v.$reset();
    },
  },
  i18n: {
    messages: {
      en: {
        valueIncomeLabel: 'Expense sum',
        valueExpenseLabel: 'Income sum',
        assetsLabel: 'Asset',
        noteLabel: 'Note',
        buttonIncomeLabel: 'Add income',
        buttonExpenseLabel: 'Add expense',
        today: '[Today]'
      },
      ru: {
        valueIncomeLabel: 'Сумма дохода',
        valueExpenseLabel: 'Сумма расхода',
        assetsLabel: 'Актив',
        noteLabel: 'Заметка',
        buttonIncomeLabel: 'Внести доход',
        buttonExpenseLabel: 'Внести расход',
        today: 'Сегодня'
      }
    }
  },
}
</script>